import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';


import { Environment } from 'src/environments/environment.model';

import { ApiNotFoundErrorResponse } from '../../../shared/models';
import {
  AdviceResponse,
  AdviceWithParamsResponse,
  GeneralQuestions,
  GeneralQuestionsRequest,
  GenericGeneralQuestionsRequest,
  GenericSoilQuestionsRequest,
  GetTgaBiomassImageResponse,
  GetTgaBiomassPotatoImageResponse,
  GetTgaSoilImageResponse,
  GetTgaVarietyPropertiesResponse,
  PlantingQuestions,
  PlantingQuestionsRequest,
  GenericPlantingQuestionsRequest,
  SoilQuestions,
  SoilQuestionsRequest,
  TgaBiomassImageParams,
  TgaBiomassPotatoImageParams,
  TgaCropFieldWithStatus,
  TgaElevationImageParams,
  TgaSoilImageParams,
  GetTgaHeightImageResponse,
  GeneralGenericQuestionsWithStatus,
  GenericSoilQuestionsWithStatus,
  GenericPlantingQuestionsWithStatus,
  GetNotificationsResponse,
  SatelliteImageDate,
  FieldObservationResponseDto,
} from '../models/models';


@Injectable({
  providedIn: 'root',
})
export class TgaApiService {
  protected http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  protected tgaBaseUrl = this.env.APIM_BASE_URL + '/arable-advice-service/';
  protected tgaDiseaseBaseUrl = this.env.APIM_BASE_URL + '/arable-disease-integration-service/';


  getGeneralQuestionsValue(cropFieldId: number, cropYear: number) {
    return this.http.get<GeneralQuestions>(`${this.tgaBaseUrl}tga-advice/general-questions/${cropFieldId}/${cropYear}`);
  }

  postGeneralQuestions(generalQuestions: GeneralQuestionsRequest) {
    return this.http.post<void>(`${this.tgaBaseUrl}tga-advice/general-questions`, generalQuestions);
  }

  getGenericGeneralQuestionsValue(cropYear: number) {
    return this.http.get<GeneralGenericQuestionsWithStatus>(`${this.tgaBaseUrl}tga-advice/generic-general-questions/${cropYear}`);
  }

  postGenericGeneralQuestions(genericGeneralQuestions: GenericGeneralQuestionsRequest) {
    return this.http.post<void>(`${this.tgaBaseUrl}tga-advice/generic-general-questions`, genericGeneralQuestions);
  }

  getSoilQuestions(cropFieldId: number, cropYear: number) {
    return this.http.get<SoilQuestions>(
      `${this.tgaBaseUrl}tga-advice/soil-specific-questions/${cropFieldId}/${cropYear}`,
    );
  }

  postSoilQuestions(soilQuestions: SoilQuestionsRequest) {
    return this.http.post<void>(`${this.tgaBaseUrl}tga-advice/soil-specific-questions`, soilQuestions);
  }

  getTgaCropFields(cropYear: number) {
    return this.http.get<TgaCropFieldWithStatus[]>(`${this.tgaBaseUrl}crop-fields/${cropYear}/tga`);
  }

  getTgaSoilImage(params: TgaSoilImageParams): Observable<GetTgaSoilImageResponse> {
    let httpParams = new HttpParams();

    if (params.width) {
      httpParams = httpParams.set('width', params.width.toString());
    } else {
      httpParams = httpParams.set('width', '800');
    }

    if (params.height) {
      httpParams = httpParams.set('height', params.height.toString());
    } else {
      httpParams = httpParams.set('height', '800');
    }

    return this.http
      .get<GetTgaSoilImageResponse>(`${this.tgaBaseUrl}crop-fields/${params.cropFieldId}/tga-soil-image`, {
        params: httpParams,
      })
      .pipe(catchError(this.handleApiError));
  }

  getTgaBiomassImage(params: TgaBiomassImageParams): Observable<GetTgaBiomassImageResponse> {
    let httpParams = new HttpParams();

    if (params.width) {
      httpParams = httpParams.set('width', params.width.toString());
    } else {
      httpParams = httpParams.set('width', '800');
    }

    if (params.height) {
      httpParams = httpParams.set('height', params.height.toString());
    } else {
      httpParams = httpParams.set('height', '800');
    }

    return this.http
      .get<GetTgaBiomassImageResponse>(`${this.tgaBaseUrl}crop-fields/${params.cropFieldId}/tga-biomass-image`, {
        params: httpParams,
      })
      .pipe(catchError(this.handleApiError));
  }

  getTgaBiomassPotatoImage(params: TgaBiomassPotatoImageParams): Observable<GetTgaBiomassPotatoImageResponse> {
    let httpParams = new HttpParams();

    if (params.width) {
      httpParams = httpParams.set('width', params.width.toString());
    } else {
      httpParams = httpParams.set('width', '800');
    }

    if (params.height) {
      httpParams = httpParams.set('height', params.height.toString());
    } else {
      httpParams = httpParams.set('height', '800');
    }

    return this.http
      .get<GetTgaBiomassPotatoImageResponse>(
        `${this.tgaBaseUrl}crop-fields/${params.cropFieldId}/tga-biomass-potato-image`,
        {
          params: httpParams,
        },
      )
      .pipe(catchError(this.handleApiError));
  }

  getGeneralAdvice(cropFieldId: number, cropYear: number): Observable<AdviceResponse[]> {
    return this.http.get<AdviceResponse[]>(
      `${this.tgaBaseUrl}tga-advice/general-advice/${cropFieldId}/${cropYear}`,
    );
  }

  getSoilAdvice(cropFieldId: number, cropYear: number): Observable<AdviceWithParamsResponse> {
    return this.http.get<AdviceWithParamsResponse>(
      `${this.tgaBaseUrl}tga-advice/soil-advice/${cropFieldId}/${cropYear}`,
    );
  }

  getPlantingAdvice(cropFieldId: number, cropYear: number): Observable<AdviceWithParamsResponse> {
    return this.http.get<AdviceWithParamsResponse>(
      `${this.tgaBaseUrl}tga-advice/planting-advice/${cropFieldId}/${cropYear}`,
    );
  }

  getCultivationAdvice(cropFieldId: number, cropYear: number): Observable<AdviceWithParamsResponse> {
    return this.http.get<AdviceWithParamsResponse>(
      `${this.tgaBaseUrl}tga-advice/cultivation-advice/${cropFieldId}/${cropYear}`,
    );
  }

  getVarietyPropertiesByEdiCode(ediCode: number): Observable<GetTgaVarietyPropertiesResponse> {
    return this.http.get<GetTgaVarietyPropertiesResponse>(`${this.tgaBaseUrl}variety-properties-tga/edicode/${ediCode}`).pipe(catchError(this.handleApiError));
  }

  getGenericSoilQuestions(cropYear: number) {
    return this.http.get<GenericSoilQuestionsWithStatus>(`${this.tgaBaseUrl}tga-advice/generic-soil-questions/${cropYear}`);
  }

  postGenericSoilQuestions(genericSoilQuestions: GenericSoilQuestionsRequest) {
    return this.http.post<void>(`${this.tgaBaseUrl}tga-advice/generic-soil-questions`, genericSoilQuestions);
  }

  getPlantingQuestions(cropFieldId: number, cropYear: number) {
    return this.http.get<PlantingQuestions>(
      `${this.tgaBaseUrl}tga-advice/planting-specific-questions/${cropFieldId}/${cropYear}`,
    );
  }

  postPlantingQuestions(plantingQuestions: PlantingQuestionsRequest) {
    return this.http.post<void>(`${this.tgaBaseUrl}tga-advice/planting-specific-questions`, plantingQuestions);
  }

  getGenericPlantingQuestions(cropYear: number) {
    return this.http.get<GenericPlantingQuestionsWithStatus>(`${this.tgaBaseUrl}tga-advice/generic-planting-questions/${cropYear}`);
  }

  postGenericPlantingQuestions(genericPlantingQuestions: GenericPlantingQuestionsRequest) {
    return this.http.post<void>(`${this.tgaBaseUrl}tga-advice/generic-planting-questions`, genericPlantingQuestions);
  }

  getTgaHeightImage(params: TgaElevationImageParams): Observable<GetTgaHeightImageResponse> {
    let httpParams = new HttpParams();

    if (params.width) {
      httpParams = httpParams.set('width', params.width.toString());
    } else {
      httpParams = httpParams.set('width', '800');
    }

    if (params.height) {
      httpParams = httpParams.set('height', params.height.toString());
    } else {
      httpParams = httpParams.set('height', '800');
    }

    return this.http
      .get<GetTgaHeightImageResponse>(`${this.tgaBaseUrl}crop-fields/${params.cropFieldId}/tga-elevation-image`, {
        params: httpParams,
      })
      .pipe(catchError(this.handleApiError));
  }

  getTgaSatelliteImageDates(cropFieldId: number, fromDate?: string, toDate?: string): Observable<SatelliteImageDate[]> {
    let params = new HttpParams();
    if (fromDate) {
      params = params.set('fromDate', fromDate);
    }
    if (toDate) {
      params = params.set('toDate', toDate);
    }

    return this.http.get<SatelliteImageDate[]>(
      `${this.tgaBaseUrl}crop-fields/${cropFieldId}/tga-satellite-images`,
      { params },
    );
  }

  getTgaSatelliteImage(cropFieldId: number, imageDate: string): Observable<Blob> {
    return this.http.get(`${this.tgaBaseUrl}crop-fields/${cropFieldId}/tga-satellite-images/${imageDate}`, {
      responseType: 'blob',
    });
  }

  getCropFieldObservations(cropFieldId: number): Observable<FieldObservationResponseDto[]> {
    return this.http.get<FieldObservationResponseDto[]>(`${this.tgaDiseaseBaseUrl}crop-fields/${cropFieldId}/observations`);
  }

  getFieldObservationImage(cropFieldId: number, observationId: number, imageId: string): Observable<Blob> {
    return this.http.get(`${this.tgaDiseaseBaseUrl}observations/${cropFieldId}/${observationId}/images/${imageId}`, {
      responseType: 'blob',
    });
  }

  private handleApiError(error: HttpErrorResponse) {
    return of({
      status: error?.error?.status || error?.status || 500,
      title: error?.error?.statusText || error?.statusText || 'Unknown Error',
      type: error?.error?.type || error?.type || 'Unknown',
      traceId: error?.error?.traceId || 'N/A',
    } as ApiNotFoundErrorResponse);
  }

  getNotifications(cropYear: number): Observable<GetNotificationsResponse> {
    return this.http
      .get<GetNotificationsResponse>(`${this.tgaBaseUrl}notifications/${cropYear}`)
      .pipe(catchError(this.handleApiError));
  }

  closeNotification(notificationId: number) {
    return this.http
      .post(`${this.tgaBaseUrl}notifications/${notificationId}/close`, {})
      .pipe(catchError(this.handleApiError));
  }

}
